import React, { Component } from "react";
import { TrafficLayer, Marker, GoogleMap } from "react-google-maps";
import { MAP } from "react-google-maps/lib/constants";
import GoogleMapsWrapper from "./GoogleMapsWrapper";
import styles from "./GoogleMapsComponent.css";
import messages from "./locales";
import toggleTrafficIcon from "./image/btn-toggle-traffic.svg?external";
import config from "../../../resources/config";
import { bringToBounds } from "./helper";
import events from "../../events";

const API_KEY = config.googleApiKey;
const GMAPS_URL = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&v=3.exp&libraries=geometry,drawing,places`;

interface Coordinate {
  lat: number;
  lng: number;
}
interface Props {
  fleetName: string;
  center: Coordinate;
  curvePathBegin: Coordinate;
  curvePathEnd: Coordinate;
  loadingElementClassName: string;
  containerElementClassName: string;
  mapElementClassName: string;
  shouldDisplayCurve: boolean;
  onMapMounted: (googleMap: GoogleMap) => void;
}

interface State {
  mapInitialized: boolean;
  showTraffic: boolean;
}

export class GoogleMapsComponent extends Component<Props, State> {
  buttonToggleTraffic = document.createElement("button");
  trafficIcon = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  map?: GoogleMap;

  static defaultProps = {
    center: { lat: -23.6648683, lng: -46.7417285 },
    curvePathBegin: undefined,
    curvePathEnd: undefined
  };

  componentWillMount() {
    this.setState({ showTraffic: false });
  }

  onMapMounted = (mapComponent: GoogleMap) => {
    if (this.map) return;
    this.map = mapComponent.context[MAP];

    this.trafficIcon.classList.add(styles.map_toggleTrafficButton_icon);
    const useTag = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "use"
    );
    useTag.setAttributeNS(
      "http://www.w3.org/1999/xlink",
      "href",
      toggleTrafficIcon + "#trafficIcon"
    );
    this.trafficIcon.appendChild(useTag);

    this.buttonToggleTraffic.classList.add(styles.map_toggleTrafficButton);
    this.buttonToggleTraffic.addEventListener(
      "click",
      this.toggleTraffic.bind(this)
    );
    this.buttonToggleTraffic.appendChild(
      document.createTextNode(messages.map_toggleTraffic_show)
    );
    this.buttonToggleTraffic.appendChild(this.trafficIcon);
    if (this.map) {
      //@ts-ignore
      this.map.controls[window.google.maps.ControlPosition.LEFT_BOTTOM].push(
        this.buttonToggleTraffic
      );
      //@ts-ignore
      this.props.onMapMounted(this);

      google.maps.event.addListener(this.map, "zoom_changed", this.reRender);
      google.maps.event.addListener(this.map, "dragend", this.reRender);
      this.setMapInitializedListener();
    }
  };

  setMapInitializedListener = () => {
    this.map &&
      google.maps.event.addListenerOnce(this.map, "idle", () => {
        this.setState({ mapInitialized: true });
      });
  };

  reRender = () => {
    this.setState(this.state);
  };

  fitBounds = (bounds: Coordinate[]) => {
    const latLngs = bounds.map(coords => new google.maps.LatLng(coords));
    const googleBounds = new google.maps.LatLngBounds();
    googleBounds.extend(latLngs[0]);
    googleBounds.extend(latLngs[1]);
    if (this.map) {
      //@ts-ignore
      this.map.fitBounds(googleBounds, 100);
    }
  };

  toggleTraffic = () => {
    this.setState({ showTraffic: !this.state.showTraffic });
    if (this.state.showTraffic) {
      this.buttonToggleTraffic.classList.add(
        styles.map_toggleTrafficButton__trafficOn
      );
      this.buttonToggleTraffic.innerHTML = messages.map_toggleTraffic_hide;
      events.mapInteraction.trafficOn(this.props.fleetName);
    } else {
      this.buttonToggleTraffic.classList.remove(
        styles.map_toggleTrafficButton__trafficOn
      );
      this.buttonToggleTraffic.innerHTML = messages.map_toggleTraffic_show;
      events.mapInteraction.trafficOff(this.props.fleetName);
    }
    this.buttonToggleTraffic.appendChild(this.trafficIcon);
  };

  curvePath = (
    begin: Coordinate,
    end: Coordinate,
    givenCurvature: number = 0.2,
    strokeColor: string = "#5d7186"
  ) => {
    if (!begin || !end) return null;
    if (!this.map) return null;

    const initialBegin = new google.maps.LatLng({
      lat: begin.lat,
      lng: begin.lng
    });
    const initialEnd = new google.maps.LatLng({
      lat: end.lat,
      lng: end.lng
    });
    const { pos1: updatedBegin, pos2: updatedEnd } = bringToBounds(
      initialBegin,
      initialEnd,
      this.map.getBounds()
    );

    if (!updatedBegin || !updatedEnd) return;

    let curvature = givenCurvature;
    if (initialBegin !== updatedBegin) {
      curvature /= 2;
    }
    if (initialEnd !== updatedEnd) {
      curvature /= 2;
    }

    const projection = this.map.getProjection();
    const p1 = projection.fromLatLngToPoint(updatedBegin);
    const p2 = projection.fromLatLngToPoint(updatedEnd);
    const p2RelativeToP1 = new google.maps.Point(p2.x - p1.x, p2.y - p1.y);
    const midpoint = new google.maps.Point(
      p2RelativeToP1.x / 2,
      p2RelativeToP1.y / 2
    );
    const orthogonal = new google.maps.Point(
      p2RelativeToP1.x,
      -p2RelativeToP1.y
    );
    const curveControl = new google.maps.Point(
      midpoint.x + curvature * orthogonal.x,
      midpoint.y + curvature * orthogonal.y
    );
    const svgPathDef =
      "M 0,0 " +
      `q ${curveControl.x},${curveControl.y}` +
      ` ${p2RelativeToP1.x},${p2RelativeToP1.y}`;
    const zoom = this.map.getZoom();
    const scale = 1 / 2 ** -zoom;

    const symbol = {
      path: svgPathDef,
      scale,
      strokeWeight: 2,
      strokeColor
    };

    return (
      <Marker
        position={updatedBegin}
        icon={symbol}
        zIndex={0}
        clickable={false}
      />
    );
  };

  render() {
    return (
      <GoogleMapsWrapper
        onMapMounted={this.onMapMounted}
        defaultZoom={10}
        defaultCenter={{
          lat: this.props.center.lat,
          lng: this.props.center.lng
        }}
        googleMapURL={GMAPS_URL}
        loadingElement={<div className={this.props.loadingElementClassName} />}
        containerElement={
          <div className={this.props.containerElementClassName} />
        }
        mapElement={<div className={this.props.mapElementClassName} />}
        defaultOptions={{
          mapTypeControlOptions: {
            position: google.maps.ControlPosition.LEFT_BOTTOM
          },
          fullscreenControl: false
        }}
        clickableIcons={false}
      >
        {this.props.children}
        {this.state.showTraffic && <TrafficLayer autoupdate />}
        {this.state.mapInitialized &&
          this.props.shouldDisplayCurve &&
          this.curvePath(this.props.curvePathBegin, this.props.curvePathEnd)}
      </GoogleMapsWrapper>
    );
  }
}
